import React, { useState, useEffect } from "react";
import {
  FixedSizeList as List,
  ListChildComponentProps,
  GridChildComponentProps,
  FixedSizeGrid as Grid
} from 'react-window';
import memoize from 'memoize-one';

import { makeStyles, createStyles } from "@material-ui/styles";
import { Theme, Tabs, Tab, TextField, Chip } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store";
import UserImg from "../../UserImg";
import { StockPhoto, UserImage, StockPhotoCategory } from "repix-common";

import { actions as userImageActions } from '../../../services/userImages';
import { actions as stockPhotoActions } from '../../../services/stockPhotos';

import StockPhotoImg from "../../StockPhotoImg";
import { loadImage, loadSvg } from "../../../utils/fabricHelper";
import { fabric } from "fabric";
import { Autocomplete } from "@material-ui/lab";
import UploadImageButton from "../../ImagePickerDialog/UploadImageButton";

const useStyles = makeStyles((theme: Theme) =>
  createStyles(
    {
      rootTabImages: {
        minHeight: 411,
      },
      tab: {
        minWidth: 20,
      },
      list: {
        padding: theme.spacing(2, 0),
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 128px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
      spacer: {
        width: theme.spacing(1),
        height: theme.spacing(1),
      },
      userImg: {
        width: 180,
        height: 180,
      },
      catList: {
        display: 'flex',
        width: '100%',
        overflow: 'auto',
        padding: theme.spacing(1, 1, 2, 1),
      },
      catItem: {
        flex: '0 0 auto',
        marginRight: theme.spacing(1),
      }
    }
  ),
);

const UserImageListItem: React.FC<ListChildComponentProps> = ({ index, style, data }) => {
  const classes = useStyles();
  const list = data.list;
  const onClick = data.onClick;
  return (
    <UserImg image={list[ index ]} onClick={onClick} className={classes.userImg} style={style} />
  );
}
const createUserImageItemData = memoize((list, onClick) => ({
  list,
  onClick,
}))

const StockPhotoListItem: React.FC<ListChildComponentProps> = ({ index, style, data }) => {
  const classes = useStyles();
  const list = data.list;
  const onClick = data.onClick;
  return (
    <StockPhotoImg
      style={style}
      photo={list[ index ]}
      onClick={onClick}
      className={classes.userImg}
    />
  );
}

const StockPhotoGridItem: React.FC<GridChildComponentProps> = ({ columnIndex, rowIndex, style, data }) => {
  const classes = useStyles();
  const list = data.list;
  const onClick = data.onClick;
  const colCount = Math.ceil(list.length / 2);
  const index = rowIndex * colCount + columnIndex; 1

  if (index >= list.length) {
    return null;
  }

  return (
    <StockPhotoImg
      style={style}
      photo={list[ index ]}
      onClick={onClick}
      className={classes.userImg}
    />
  );
}

const createStockPhotoItemData = memoize((list, category, onClick) => ({
  list: list[ category.id ],
  onClick,
}))

interface Props {
  onAdd?: () => void;
  horizontal?: boolean;
  activeCanvas: fabric.Canvas;
  categoriesAsChips?: boolean;
}



const TabImages: React.FC<Props> = ({ onAdd, horizontal, activeCanvas, categoriesAsChips }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [ tab, setTab ] = useState(0);
  const [ category, setCategory ] = useState<StockPhotoCategory>();

  const userImages = useSelector((state: RootState) => state.userImages);
  const stockPhotos = useSelector((state: RootState) => state.stockPhotos);

  useEffect(() => {
    dispatch(userImageActions.loadList());
  }, []);

  useEffect(() => {
    if (typeof category === 'undefined' && stockPhotos.categories.length > 0) {
      setCategory(stockPhotos.categories[ 0 ]);
    }
  }, [ stockPhotos.categories.length ])

  useEffect(() => {
    if (!category) {
      dispatch(stockPhotoActions.loadCategories());
      return;
    }

    dispatch(stockPhotoActions.loadList(category.id));
  }, [ category ]);

  if (!activeCanvas) {
    return null;
  }


  const onTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  const onUserImageClick = async (img: UserImage) => {
    onAdd && onAdd();
    const imgUrl = `${process.env.CDN_URL}/${img.path}`;
    let image: fabric.Object;
    if (img.fileType.includes('svg')) {
      image = await loadSvg(imgUrl);
    }
    else {
      image = await loadImage(imgUrl);
      if ((image.width || 1) > activeCanvas?.getScaledWidth()) {
        image.scaleToWidth(activeCanvas.getScaledWidth() - 100);
      }
      if ((image.height || 1) > activeCanvas?.getScaledHeight()) {
        image.scaleToWidth(activeCanvas.getScaledWidth() - 100);
      }
    }
    activeCanvas?.add(image);
    activeCanvas?.updated();
  }

  const onStockPhotoClick = async (stockPhoto: StockPhoto) => {
    onAdd && onAdd();
    const imgUrl = `${process.env.CDN_URL}/${stockPhoto.large.path}`;
    const image = await loadImage(imgUrl);

    // const clipPath = new fabric.Path('M392.8 121.6c4.5-1.7 10.5-.9 15.6-2.6 10.8-3.7 19.4-9.6 25.7-16.3-1.3-6.9-1.7-10.7-.8-16.5-.8-.4-2.5.7-2.6-.9-.2-4.6-2.4-6.3-4.5-10.2-16.1 2.3-45.4 14.9-62.8 13.8 10.7-7.6 18.4-16.1 18.6-27.3-.9-.9-6-1.5-7.2-1.1-4.1-.8-8.3-4.5-10.1-6.8-1.7 1.1-2-.1-3.5.7-6.4-4.9-19.9 2.5-26.4-2.1 3-7.9-3.1-9.7-7.2-13.5 1.5-6.2-2.2-9.5-5.6-15.3-4.2 2-3.9-1.1-3.4-3.1-3.1.9-5-.3-6.7-1.9-6 2.3-11.5 2.4-16.3 2.9-24.5 2.6-54.9 9.2-76.9 10.3 20.2-8.6 43.6-14.1 58.5-21.5.5-4.2.4-6.1.1-10-16.1-1.4-40.8 6.3-63.7 13.5C160.5 30.4 96.5 49.4 66.4 79c.8 4.4-.5 10.1 2.8 13.9-11 6-23.6 11.4-34.3 17.6.4 5.2 3.1 10.6 4.2 11.5-3.5 1.9 1.9.5 1.7 2.5-1.5.7-.5 2.4-2.7 2.9-8.1-1.3-17.1 5-25.4 6.9-.3 1.3 2.7 1.1.8 2.2-5.5 1.3-6.1 4.2-11.6 5.5-.2 1.2-.4 2.4-.1 3.8.8 1 5.6-.2 4.3 1.5-2.9 1-6.3.7-6.2 3.5 1.6 1.8 4.9.6 5.8 3.5-.7 1.4-2.9 2.2-2.3 4 4.7-.1 8.9.8 13.8.5-1.5.8 1.2 2.6 5.2 1.8-.2 1.4-3.7 1.6-3.4 3.1 5.8-1.1 7.4-3.3 12.8-4.1-3.6 2.9-8.2 7.4-12.7 8.4-1.3 1.8-.8 4.2-1.5 6.1-1.2-.4-1.1 5.9 2 7.4 3.9-1 12.7-.5 18.4.7-.4 1.1-1.7 1.9-1 3.5 1.9.7 6.5-3.3 8.4-1.8 2.8 1-2.2 2-.8 4 18.6-9.3 33.5-11 52.4-15.5-21.1 9.2-49.6 34-50.1 42.1 2.6 0 4.8.6 7.2 1.1-.8 2.1-2 4.1-2.2 6.4 1.3-2.3 5.9-3.4 6.3.8-3.2.3-3.2 1.3-3.2 3.7 3.3.4 8.4-2.5 10 .6-2.1 1.3-2.9.1-4.9 1.2.1 2 2.1.5 2.8 1.5-5.8 2.4-5.5 3.3-7.4 6.5-1.6-2.6-5.8 14.1-2.4 13.9-2.8 2.9 2.6 1.2 3.9 2.3-1.2 2.1 1.5 2.7 1.8 4.9 1.5.4 4.4-1.7 4.3 1.5-2.9.3-2-.5-4 1.5-.7-.7-2.2-1.4-3.7.1 1.1 2.1.7 3.7.5 5.4 5.3-.9 1.6.9 6.5 1.3 1.5 1.8-.7 2.3-1 3.5 9.6.3 14.6 4.3 22.8 6.2 0 1.8 1.5 1.1 1.7 2.5 15.6-1.8 25.9-.1 43.9-5.6-2.2-2-8.4-.4-9.6-1.4 12.2-5.1 27.1-6.9 41.7-9.8 14-2.7 27.8-1.8 41.6-7.9 3.3 1.3 5.9-1.2 9-2.1 16.2-4.9 36.8-8.8 52.8-10.1 2.8-.2 6-1.8 7.3-2.7 5.2.8 14.2-2.6 18.9-3.9-.2-.9-1-1.9-.1-2.4 6.5-1.5 15.7-3.1 21.7-6.7-.8-.4-2.5.7-2.6-.9 5.7-1.4 12.7-2 18.1-4.2-1.1-.2-2.3-.2-3.3-.7 6.7-5.1 9.5-7.6 18.7-8.7-.7-3 1.4-4.9 6.1-6 1.3-1.8-2.7-1.1-1.2-3.3 16.7-2.2 21.3-17.2 29-25.6.3-.3 1.6-1.1 1.8-1.3 10.6-6.4 23.7-7.3 31.8-16.1 3.1-6.7 11.3-15.2 2.2-20.7 1-1.4.5-3.3-.5-5.4-4.1 1.1-23-2.6-27.2-4.3.9-2.1-1-4.2-.5-5.4 3.9-.2 5-1.7 7.3-2.6z', {
    //   top: 0,
    //   left: 0,
    //   originX: 'center',
    //   originY: 'center',
    //   stroke: '#000000',
    //   fill: '#FFFFFF',
    // });
    // clipPath.scaleToWidth(image.width as number);

    // image.set({
    //   clipPath,
    // });

    if ((image.width || 1) > activeCanvas?.getScaledWidth()) {
      image.scaleToWidth(activeCanvas.getScaledWidth() - 100);
    }
    if ((image.height || 1) > activeCanvas?.getScaledHeight()) {
      image.scaleToWidth(activeCanvas.getScaledWidth() - 100);
    }

    activeCanvas?.add(image);
    activeCanvas?.updated();


  }

  const onFileSelect = (file: File, src: string) => {
    // if (file.size >)
    dispatch(userImageActions.uploadImage({
      file,
      src,
      type: 'other',
    }))
  }

  if (!category) {
    return null;
  }


  const stockPhotoItemData = createStockPhotoItemData(stockPhotos.list, category, onStockPhotoClick)
  const userImageItemData = createUserImageItemData(userImages.images, onUserImageClick);


  return (
    <div className={classes.rootTabImages}>
      <Tabs value={tab} onChange={onTabChange} variant="fullWidth">
        <Tab label="Uploads" className={classes.tab} />
        <Tab label="Stock" className={classes.tab} />
      </Tabs>
      <div className={classes.list}>
        {
          tab === 0 ? (
            <React.Fragment>
              <UploadImageButton imageType="other" />
              <div className={classes.spacer} />
              <div className={classes.spacer} />
              <List
                height={horizontal ? 300 : window.innerHeight - 120}
                width={horizontal ? window.innerWidth : 200}
                layout={horizontal ? 'horizontal' : 'vertical'}
                itemCount={userImages.images.length}
                itemSize={120}
                itemData={userImageItemData}
              >
                {UserImageListItem}
              </List>
            </React.Fragment>
          ) : null
        }

        {
          tab === 1 ? (
            <React.Fragment>
              {
                !categoriesAsChips ? (
                  <Autocomplete
                    options={stockPhotos.categories}
                    getOptionLabel={(option) => option.name}
                    style={{ width: horizontal ? window.innerWidth - 36 : 200 }}
                    renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
                    onChange={(e, newValue) => { setCategory(newValue) }}
                    value={category}
                    disableClearable
                  />
                ) : (
                    <div className={classes.catList}>
                      {
                        stockPhotos.categories.map(cat => (
                          <Chip
                            key={cat.id}
                            label={cat.name}
                            className={classes.catItem}
                            onClick={() => setCategory(cat)}
                            color={category.id === cat.id ? 'secondary' : 'default'}
                          />
                        ))
                      }
                    </div>
                  )
              }
              <div className={classes.spacer} />
              <div className={classes.spacer} />

              {
                horizontal ? (
                  <Grid
                    columnCount={stockPhotos.list[ category.id ] ? Math.ceil(stockPhotos.list[ category.id ].length / 2) : 0}
                    rowCount={2}
                    columnWidth={window.innerWidth / 2.5}
                    rowHeight={150}
                    width={window.innerWidth}
                    height={300}
                    itemData={stockPhotoItemData}
                  >
                    {StockPhotoGridItem}
                  </Grid>

                ) : (
                    <List
                      layout="vertical"
                      height={window.innerHeight - 120}
                      width={200}
                      itemCount={stockPhotos.list[ category.id ] ? stockPhotos.list[ category.id ].length : 0}
                      itemSize={120}
                      itemData={stockPhotoItemData}
                    >
                      {StockPhotoListItem}
                    </List>
                  )
              }
            </React.Fragment>
          ) : null
        }

      </div>
    </div>
  );
};

export default React.memo(TabImages);